/**
 * Passo 3 di "Scrivi a piu' persone": riepilogo prima di confermare.
 * Quanti, da quale numero, quando, esclusi, anteprima con un nome vero.
 */
import { useMemo } from 'react';
import { etichettaNumero } from '@/lib/services/whatsapp/phoneDigits';
import { durationLabel, firstSendTime, isNightInItaly, BULK_LIMITS } from '@/lib/services/whatsapp/bulkSend/bulkSchedule';
import { previewFor } from '@/lib/services/whatsapp/bulkSend/bulkMessage';
import { EXCLUSION_LABELS, type ExclusionReason } from '@/lib/services/whatsapp/bulkSend/bulkRecipients';
import type { BulkSendState } from './useBulkSend';

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3 py-1.5 border-b border-[rgba(55,53,47,0.06)] dark:border-white/5 last:border-0">
    <span className="w-32 flex-shrink-0 text-xs text-[#787774]">{label}</span>
    <span className="text-sm text-[#37352F] dark:text-white min-w-0">{children}</span>
  </div>
);

export function BulkSummaryStep({ s }: { s: BulkSendState }) {
  const { recipients, excluded, summary } = s.built;
  // Anteprima: il primo destinatario con un nome, cosi' si vede la sostituzione vera.
  const sample = recipients.find((r) => r.firstName) ?? recipients[0];
  const tpl = s.template ? { name: s.template.name, language: s.template.language, components: s.template.components } : null;
  const preview = sample ? previewFor(s.channel, sample, { text: s.text, template: tpl, vars: s.vars }) : '';
  const start = useMemo(() => firstSendTime(new Date()), []);
  const slitta = isNightInItaly(new Date(Date.now() + 60_000));
  const lim = BULK_LIMITS[s.channel];
  const numero = s.config ? `${s.config.displayName || etichettaNumero(s.config.phoneNumber)} (${s.config.serviceName === 'waha' ? 'Standard' : 'Ufficiale'})` : '';

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 px-3 py-1">
        <Row label="Destinatari">{recipients.length === 1 ? '1 persona' : `${recipients.length} persone`}</Row>
        <Row label="Dal numero">{numero}</Row>
        <Row label="Messaggio">{s.channel === 'standard' ? 'Testo libero' : `Modello "${s.template?.name}"`}</Row>
        <Row label="Partenza">
          {start.toLocaleString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          {slitta && <span className="block text-xs text-[#787774]">Di sera e di notte non si invia: si parte alle 8:00.</span>}
        </Row>
        <Row label="Durata">
          {durationLabel(recipients.length, s.channel)}
          {recipients.length > 1 && (
            <span className="block text-xs text-[#787774]">Pausa casuale di {lim.minGapSec}-{lim.maxGapSec} secondi fra un messaggio e l'altro.</span>
          )}
        </Row>
        {excluded.length > 0 && (
          <Row label="Esclusi">
            {(Object.entries(summary) as Array<[ExclusionReason, number]>).map(([reason, n]) => (
              <span key={reason} className="block text-xs">{n}: {EXCLUSION_LABELS[reason]}</span>
            ))}
          </Row>
        )}
      </div>

      {sample && (
        <div>
          <p className="text-xs font-medium text-[#787774] mb-1">Anteprima per {sample.name}</p>
          <div className="rounded-lg bg-[#DCF8C6] dark:bg-emerald-900/30 px-3 py-2 text-sm text-[#111B21] dark:text-white whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
            {preview}
          </div>
        </div>
      )}

      <p className="text-xs text-[#787774]">
        I messaggi partono dal server anche se chiudi la pagina. Nel resoconto vedi inviati e non inviati, e puoi annullare quelli non ancora partiti.
      </p>
    </div>
  );
}
